import React, { useContext } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CardActions from '@mui/material/CardActions';
import Rating from '@mui/material/Rating';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LocationOnIcon from '@mui/icons-material/LocationOnSharp';
import ShareIcon from '@mui/icons-material/Share';

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';


import Map, { Marker, Popup } from "react-map-gl/mapbox";
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './styles.css';

import chevron from '../../../assets/chevron_pin.png'
import { Link } from "react-router-dom";
import { Store } from '../../store/Store';

import { generateRandomNumber } from "../../../utils";
import { toLatLng, toViewState } from "../../../utils/geo";





// eslint-disable-next-line import/no-webpack-loader-syntax
import MapboxWorker from 'worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker';
mapboxgl.workerClass = MapboxWorker

const MAPBOX_TOKEN = 'pk.eyJ1IjoiY25vdGV6dyIsImEiOiJjbGwyNHlzYW8wNDU1M3Bxc3RjNWliZmN1In0.JuNYprf-c08-veQTcQXluA';

const drawerWidth = '40vw';

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconEmpty .MuiSvgIcon-root': {
    color: theme.palette.action.disabled,
  },
}));

const customIcons = {
  1: {
    icon: <AttachMoneyIcon color="success" />,
    label: 'Bargain',
  },
  2: {
    icon: <AttachMoneyIcon color="success" />,
    label: 'Affordable',
  },
  3: {
    icon: <AttachMoneyIcon color="success" />,
    label: 'Pricy',
  },
  4: {
    icon: <AttachMoneyIcon color="success" />,
    label: 'Satisfied',
  },
  5: {
    icon: <AttachMoneyIcon color="success" />,
    label: 'Very Satisfied',
  },
};

function IconContainer(props) {
  const { value, ...other } = props;
  return <span {...other}>{customIcons[value].icon}</span>;
}

/**
 * @param center  where the map opens. Separate from `ipInfo` because that one
 *   also plants the "you are here" chevron: passing a searched-for destination
 *   as `ipInfo` would mark it as the traveller's own position. Falls back to
 *   `ipInfo` so the existing behaviour is unchanged when only that is given.
 */
export default function MapContainer({ open, providers, ipInfo, center }) {
  const viewPoint = center ?? ipInfo;
  const { state: { serviceProvider } } = useContext(Store);

  const [costRating, setCostRating] = React.useState({
    cost: 0,
    rating: 1
  });
  const [popupInfo, setPopupInfo] = React.useState(null);
  const [providerPins, setPins] = React.useState([]);
  // Listings store `coordinates` as an object; older rows stored an array.
  // toViewState reads either, and yields null when there is no position.
  const [coordinates, setCoordinates] = React.useState(() => toViewState(viewPoint, 12));

  const pins = (info) =>
    providers.map((city, index) => {
      const point = toLatLng(city);
      // A listing with no usable position is skipped, not pinned at NaN.
      if (!point) return null;
      return (
        <Marker
          key={`marker-${city.name}-${city.description}`}
          longitude={point.longitude}
          latitude={point.latitude}
          anchor="bottom"
          onClick={e => {
            e.originalEvent.stopPropagation();
            setPopupInfo(city);
          }}
        >
          <LocationOnIcon style={{ fontSize: info && info.name === city.name ? '6.5rem' : '3.5rem', color: '#000', cursor: 'pointer' }} />
        </Marker>
      )
    })

  // providers
  React.useEffect(() => {

    if (serviceProvider) {

      setCostRating({
        cost: generateRandomNumber(5),
        rating: generateRandomNumber(5)
      })
      const view = toViewState(serviceProvider, 13.5)
      if (view) setCoordinates(view)
      setPopupInfo(serviceProvider)
      setPins(pins(ipInfo))
    }
    // Deliberately keyed on the SELECTION alone. `pins` is rebuilt every render
    // and reads `ipInfo`; listing either would re-run this on unrelated changes
    // and recentre the map under the traveller. Unlike MapView's, this pins()
    // really does use its argument — it enlarges the selected provider's marker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceProvider]);

  // Unconditional: the old guard only redrew when there was at least one
  // provider, so a search that came back empty kept the previous search's pins
  // on the map.
  React.useEffect(() => {
    setPins(pins(ipInfo))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers])

  /**
   * Recentre when the SEARCH moves, not when the traveller does.
   *
   * Keyed on the centre's coordinates rather than the object, so panning and
   * zooming (which rewrite `coordinates` on every frame) do not fight the map
   * back to where it started — while a second search from the results page,
   * which does not remount this component, still moves it.
   */
  const centrePoint = toLatLng(viewPoint);
  const centreKey = centrePoint ? `${centrePoint.latitude},${centrePoint.longitude}` : null;

  React.useEffect(() => {
    if (!centreKey) return;
    const view = toViewState(viewPoint, 12);
    if (view) setCoordinates(view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centreKey])

  return (
    <Box
      sx={{
        width: drawerWidth,
        overflow: 'hidden',
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box"
        },
        position: "relative"
      }}
      variant="persistent"
      anchor="left"
      open={open}

    >
      {coordinates &&
        <Map
          {...coordinates}
          onMove={evt => setCoordinates(evt.viewState)}
          style={{ width: drawerWidth, height: "100vh" }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          mapboxAccessToken={MAPBOX_TOKEN}
        >
          {toLatLng(ipInfo) && (
            <Marker longitude={toLatLng(ipInfo).longitude} latitude={toLatLng(ipInfo).latitude}>
              <img src={chevron} alt="" style={{ width: '3rem' }} />
            </Marker>
          )}
          {providerPins}

          {popupInfo && toLatLng(popupInfo) && (
            <Popup
              anchor="top"
              longitude={toLatLng(popupInfo).longitude}
              latitude={toLatLng(popupInfo).latitude}
              onClose={() => setPopupInfo(null)}
              style={{ background: 'transparent' }}
            >
              <Card sx={{ maxWidth: 345, borderRadius: 3, cursor: 'pointer', margin: 0, padding: 0 }}>
                <CardHeader
                  title={popupInfo.name}
                  subheader={popupInfo.location}
                />
                <CardMedia
                  component="img"
                  height="104px"
                  width='104px'
                  image={popupInfo.images?.[0] || ''}
                  alt={popupInfo.name}
                />

                <CardContent sx={{ padding: '6px 12px' }}>
                  <Box component='div' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '6px 0px' }}>
                    <StyledRating
                      name="cost-rating"
                      value={costRating.cost}
                      IconContainerComponent={IconContainer}
                      getLabelText={(value) => customIcons[value].label}
                      size="large"
                    />
                    <Rating name="read-only" value={costRating.rating} readOnly size="large" />
                  </Box>
                  <Typography variant="h6" color="text.secondary">
                    {popupInfo.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ display: 'flex', justifyContent: 'space-between' }} disableSpacing>
                  <IconButton aria-label="share">
                    <ShareIcon />
                  </IconButton>

                  {/* <Button className='btn-contain-outlined' variant="contained" startIcon={<CalendarMonthOutlinedIcon />}>
                    Booking
                  </Button> */}
                  {/* Was <ServiceProviderView serviceProvider={serviceProvider} />,
                      which could never render: it was passed no `open` prop, so the
                      Dialog stayed closed — and it read the Store's selected provider
                      rather than the pin actually being shown. A link to the pin's
                      own listing is what this popup wanted. */}
                  {popupInfo.id && (
                    <Button component={Link} to={`/listing/${popupInfo.id}`} size="small">
                      View details
                    </Button>
                  )}

                </CardActions>
              </Card>
            </Popup>
          )}
        </Map>}

    </Box>
  );
}
